import { create } from 'zustand';

export type EmbedSource = 'optisigns' | null;

interface EmbedState {
  isEmbedded: boolean;
  embedSource: EmbedSource;
  workspaceId: string | null;
  parentOrigin: string | null;
  /** Firebase ID token from parent (scio3), used for API auth in embed mode */
  parentToken: string | null;
  /**
   * True when an OptiSigns embed user doesn't have sufficient role to access OptiDev.
   * They were not pre-created by the webhook (role below sync threshold).
   * UI should show "Contact your admin" instead of the app.
   */
  needsAdminAccess: boolean;
  /**
   * True when user is authenticated in embed mode but no OptiDev workspace exists yet.
   * Detected via empty workspaceId in ON_INIT bridge response.
   * UI shows EmbedOnboarding; cleared once PROVISION_WORKSPACE succeeds.
   */
  needsProvisioning: boolean;
  setEmbedMode: (source: EmbedSource, workspaceId: string | null) => void;
  setParentOrigin: (origin: string) => void;
  setParentToken: (token: string) => void;
  setNeedsAdminAccess: (value: boolean) => void;
  setNeedsProvisioning: (value: boolean) => void;
  clearEmbedMode: () => void;
}

/**
 * Detect embed params from URL on initial load.
 * ?embed=optisigns&ws=xxx
 */
function detectEmbed(): { source: EmbedSource; workspaceId: string | null } {
  if (typeof window === 'undefined') return { source: null, workspaceId: null };
  const params = new URLSearchParams(window.location.search);
  const embed = params.get('embed');
  if (embed === 'optisigns') {
    return { source: 'optisigns', workspaceId: params.get('ws') };
  }
  return { source: null, workspaceId: null };
}

const initial = detectEmbed();

/**
 * Embed state store — session-only, NOT persisted to localStorage.
 * Tracks whether the app is running inside an iframe (e.g. scio3).
 */
export const useEmbedStore = create<EmbedState>((set) => ({
  isEmbedded: initial.source !== null,
  embedSource: initial.source,
  workspaceId: initial.workspaceId,
  parentOrigin: null,
  parentToken: null,
  needsAdminAccess: false,
  needsProvisioning: false,
  setEmbedMode: (source, workspaceId) =>
    set({ isEmbedded: source !== null, embedSource: source, workspaceId }),
  setParentOrigin: (origin) => set({ parentOrigin: origin }),
  setParentToken: (token) => set({ parentToken: token }),
  setNeedsAdminAccess: (value) => set({ needsAdminAccess: value }),
  setNeedsProvisioning: (value) => set({ needsProvisioning: value }),
  clearEmbedMode: () =>
    set({ isEmbedded: false, embedSource: null, workspaceId: null, parentOrigin: null, parentToken: null, needsAdminAccess: false, needsProvisioning: false }),
}));
